<template>
    
    <div  class="domain-checkout">
        <el-card v-if="!isSuccess" class="checkout-card">
            <div slot="header" class="checkout-header">
                <h2 class="checkout-title">{{ $t('domain.checkoutTitle') }}</h2>
            </div>

            <!-- 订单摘要 -->
            <div class="order-summary">
                <h3 class="section-title">{{ $t('domain.orderSummary') }}</h3>
                <el-table :data="cartItems" class="order-table">
                    <el-table-column prop="domain" :label="$t('domain.domainInfo')">
                        <template slot-scope="scope">
                            <div class="domain-info">
                                <span class="domain-name">{{ scope.row.domain }}</span>
                                <span class="domain-period">{{ scope.row.period }} {{ $t('domain.year') }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="price" :label="$t('domain.unitPrice')" width="200" align="center">
                        <template slot-scope="scope">
                            <div v-if="calculateDiscountPrice(scope.row.domain, scope.row.price)" class="checkout-discount-price">
                                <div class="checkout-original-price">${{ scope.row.price }}</div>
                                <div class="checkout-discount-price-value">${{ calculateDiscountPrice(scope.row.domain, scope.row.price).discountPrice }}</div>
                            </div>
                            <div v-else class="checkout-normal-price">
                                <span class="price">${{ scope.row.price }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="subtotal" :label="$t('domain.subtotal')" width="200" align="right">
                        <template slot-scope="scope">
                            <div v-if="calculateDiscountPrice(scope.row.domain, scope.row.price)" class="checkout-discount-price">
                                <div class="checkout-original-price">${{ scope.row.price }}</div>
                                <div class="checkout-discount-price-value">${{ calculateDiscountPrice(scope.row.domain, scope.row.price).discountPrice }}</div>
                            </div>
                            <div v-else class="checkout-normal-price">
                                <span class="price">${{ scope.row.price }}</span>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>

                <div v-if="hasAnyDiscount" class="original-total-price">
                    <span>原价总计:</span>
                    <span>${{ originalTotalPrice }}</span>
                </div>
                <div v-if="hasAnyDiscount" class="discount-row">
                    <span>优惠总额:</span>
                    <span class="discount-amount">-${{ totalDiscount }}</span>
                </div>
                <div class="total-payment">
                    <span>{{ $t('domain.totalPayment') }}:</span>
                    <span class="total-price">${{ totalPrice }}</span>
                </div>
                <div class="current-balance">
                    <span>{{ $t('message.currentBalance') || '当前余额' }}:</span>
                    <span class="balance-amount">${{ userBalance.toFixed(2) }}</span>
                </div>
                <div v-if="isBalanceInsufficient" class="balance-insufficient-tip">
                    <el-alert :title="$t('message.insufficientBalance') || '当前账户余额不足'" type="warning" show-icon
                        :closable="false">
                        <template slot="title">
                            {{ $t('message.insufficientBalance') || '当前账户余额不足' }}，
                            <el-button type="text" @click="createModel = true">{{ $t('message.goToRecharge') || '去充值' }}</el-button>
                        </template>
                    </el-alert>
                </div>
            </div>

            <!-- 联系信息模板 -->
            <div class="contact-info">
                <h3 class="section-title">{{ $t('domain.contactInfo') }}</h3>
                <el-form :model="contactForm" :rules="contactRules" ref="contactForm" label-width="120px"
                    class="checkout-form">
                    <el-form-item :label="$t('domain.templateSelect')" prop="templateId">
                        <el-select v-model="contactForm.templateId" style="width: 100%" @change="handleTemplateChange"
                            :loading="templateLoading">
                            <el-option v-for="item in templateList" :key="item.id" :label="item.name" :value="item.id">
                                <div class="template-option">
                                    <span class="template-name">{{ item.name }}</span>
                                    <span class="template-contact">{{ item.contact }} | {{ item.email }}</span>
                                </div>
                            </el-option>
                        </el-select>
                    </el-form-item>

                    <div v-if="selectedTemplate" class="template-detail">
                        <el-row :gutter="20">
                            <el-col :span="12">
                                <div class="detail-item">
                                    <span class="label">{{ $t('domain.contactName') }}：</span>
                                    <span class="value">{{ selectedTemplate.name }}</span>
                                </div>
                            </el-col>
                            <el-col :span="12">
                                <div class="detail-item">
                                    <span class="label">{{ $t('domain.email') }}：</span>
                                    <span class="value">{{ selectedTemplate.email }}</span>
                                </div>
                            </el-col>
                        </el-row>
                        <el-row :gutter="20">
                            <el-col :span="12">
                                <div class="detail-item">
                                    <span class="label">{{ $t('domain.phone') }}：</span>
                                    <span class="value">{{ selectedTemplate.mobile }}</span>
                                </div>
                            </el-col>
                            <el-col :span="12">
                                <div class="detail-item">
                                    <span class="label">{{ $t('domain.address') }}：</span>
                                    <span class="value">{{ selectedTemplate.address }}</span>
                                </div>
                            </el-col>
                        </el-row>
                        <el-row :gutter="20">
                            <el-col :span="8">
                                <div class="detail-item">
                                    <span class="label">{{ $t('domain.province') }}：</span>
                                    <span class="value">{{ selectedTemplate.sp || '--' }}</span>
                                </div>
                            </el-col>
                            <el-col :span="8">
                                <div class="detail-item">
                                    <span class="label">{{ $t('domain.city') }}：</span>
                                    <span class="value">{{ selectedTemplate.city || '--' }}</span>
                                </div>
                            </el-col>
                            <el-col :span="8">
                                <div class="detail-item">
                                    <span class="label">{{ $t('domain.zipCode') }}：</span>
                                    <span class="value">{{ selectedTemplate.pc || '--' }}</span>
                                </div>
                            </el-col>
                        </el-row>
                    </div>

                    <div v-if="!templateList.length" class="no-template-tip">
                        <el-alert title="没有可用的联系人模板" type="warning" :closable="false">
                            <template slot="title">
                                {{ $t('domain.noTemplateAvailable') }}
                                <el-button type="text" @click="goToTemplateManage">{{ $t('domain.goToCreateTemplate')
                                }}</el-button>
                            </template>
                        </el-alert>
                    </div>
                </el-form>
            </div>

            <!-- 操作按钮 -->
            <div class="checkout-actions">
                <el-button @click="backToCart">{{ $t('domain.backToCart') }}</el-button>
                <el-button type="primary" @click="confirmOrder" :loading="isSubmitting" :disabled="isBalanceInsufficient">{{ $t('domain.confirmOrder')
                }}</el-button>
            </div>
        </el-card>
        <!-- 成功提示 -->
         <el-card v-else class="checkout-card">
            <el-result 
            icon="success" 
            :title="$t('domain.orderSuccess')">
                <template slot="extra">
                    <el-button type="primary" @click="gotoDomainRegister">
                        {{ $t('domain.gotoDomainRegister') }}
                    </el-button>
                    <el-button @click="goToDomainList">
                        {{ $t('domain.goToDomainList') }}
                    </el-button>
                </template>
            </el-result>
        </el-card>
         



        <RechargeDialog :is_phone="false" :check_id="Number(check_id)" :recModel="createModel" @hide="createModel = false"  @successSub="successSub" @changeStatus="changeStatus"></RechargeDialog>
    </div>
    
</template>

<script>
import QrcodeVue from 'qrcode.vue';
import RechargeDialog from '@/components/common/RechargeDialog.vue';

export default {
    name: 'DomainCheckout',
    components: { QrcodeVue, RechargeDialog },
    data() {
        return {
            cartItems: [],
            paymentMethod: 'alipay',
            isSubmitting: false,
            isSuccess: false,
            domainDiscountData: {}, // 域名折扣数据
            templateList: [], // 模板列表
            templateLoading: false, // 模板加载状态
            selectedTemplate: null, // 当前选中的模板
            contactForm: {
                templateId: '' // 选中的模板ID
            },
            contactRules: {
                templateId: [
                    { required: true, message: this.$t('domain.templateRequired'), trigger: 'change' }
                ]
            },
            userBalance: 0, // 用户余额
            isBalanceInsufficient: false, // 余额是否不足
            createModel: false,
            check_id: 0,
        }
    },
    computed: {
        // 计算折扣后总价
        totalPrice() {
            return this.cartItems.reduce((total, item) => {
                const discountInfo = this.calculateDiscountPrice(item.domain, item.price);
                const itemPrice = discountInfo ? discountInfo.discountPrice : parseFloat(item.price);
                return total + itemPrice;
            }, 0).toFixed(2);
        },
        // 计算原价总价
        originalTotalPrice() {
            return this.cartItems.reduce((total, item) => total + parseFloat(item.price), 0).toFixed(2);
        },
        // 计算优惠总额
        totalDiscount() {
            return (parseFloat(this.originalTotalPrice) - parseFloat(this.totalPrice)).toFixed(2);
        },
        // 是否有任何折扣
        hasAnyDiscount() {
            return this.cartItems.some(item => this.calculateDiscountPrice(item.domain, item.price));
        },
        // 计算最低充值金额
        minDepositAmount() {
            // 计算订单金额与当前余额的差额，如果余额足够则返回0
            const neededAmount = parseFloat(this.totalPrice) - this.userBalance;
            return neededAmount > 0 ? neededAmount.toFixed(2) : '0.00';
        }
    },
    created() {
        this.loadCartItems();
        this.checkLoginStatus();
        this.fetchTemplateList();
        this.fetchUserBalance(); // 获取用户余额
        this.loadDomainDiscountData(); // 获取域名折扣数据
    },
    methods: {
        successSub(id) {
            this.createModel = true
            this.check_id = id
        },
        changeStatus(id, status) {
            console.log(id, status)
        },
        async fetchUserBalance() {
            try {
                let res = await this.axios.post('/admin/user/QueryUserBalance', {}, { token: true });
                // console.log('QueryUserBalance', res);

                if (res.status === 200 && res.data && res.data.code === 1) {
                    this.userBalance = parseFloat(res.data.data.list.balance) || 0;
                    this.checkBalance();
                } else {
                    this.$message.error(this.$t('message.fetchBalanceFailed') || '获取用户余额失败');
                    this.isBalanceInsufficient = true; // 默认视为不足
                }

                res = await this.axios.post('/admin/user/QueryChargeConfig', {}, { token: true });
                
                if (res.status === 200 && res.data && res.data.code === 1) {
                    this.depositWalletAddress = res.data.data.list[0].usdt_url;
                }
            } catch (error) {
                console.error('获取用户余额失败', error);
                this.$message.error(this.$t('message.fetchBalanceFailed') || '获取用户余额失败');
                this.isBalanceInsufficient = true; // 发生错误时，默认视为不足
            }
        },
        checkBalance() {
            if (this.userBalance < parseFloat(this.totalPrice)) {
                this.isBalanceInsufficient = true;
            } else {
                this.isBalanceInsufficient = false;
            }
        },
        checkLoginStatus() {
            // 检查用户是否已登录
            const token = window.sessionStorage.getItem('token');
            if (!token) {
                // 保存购物车数据和当前状态
                localStorage.setItem('checkoutRedirect', 'true');

                // 提示用户需要登录
                this.$message.info(this.$t('message.noLogin'));

                // 跳转到登录页面，使用完整路径
                this.$router.push('/login?redirect=domain_checkout');
                return;
            }
        },
        loadCartItems() {
            const savedItems = localStorage.getItem('domainCartItems');
            if (savedItems) {
                try {
                    this.cartItems = JSON.parse(savedItems);
                } catch (e) {
                    this.cartItems = [];
                    console.error('Failed to parse cart items', e);
                }
            }

            // 如果购物车为空，返回域名注册页面
            if (this.cartItems.length === 0) {
                this.$message.warning('购物车为空，请先添加域名');
                this.backToCart();
            } else {
                this.checkBalance(); // 加载完购物车后检查余额
            }
        },
        // 获取模板列表
        fetchTemplateList() {
            this.templateLoading = true;

            // 发起API请求获取模板列表
            this.axios.post('/admin/domain/templateList', {
                page: 1,
                limit: 100 // 获取足够多的模板
            }, {
                token: true
            }).then(res => {
                this.templateLoading = false;

                if (res.status === 200 && res.data && res.data.data && res.data.code == 1) {
                    const { list, total } = res.data.data.list;
                    // 转换API返回的数据格式为组件需要的格式
                    console.log('template', list);

                    this.templateList = list.map(item => ({
                        id: item.id,
                        name: item.name,
                        contact: item.contact,
                        email: item.email,
                        mobile: item.mobile,
                        registrantId: item.registrant_id,
                        address: item.address || '',
                        province: item.province || '',
                        city: item.city || '',
                        pc: item.postcode || ''
                    }));

                    // 如果有模板，默认选中第一个
                    if (this.templateList.length > 0) {
                        this.contactForm.templateId = this.templateList[0].id;
                        this.selectedTemplate = this.templateList[0];
                    }
                } else {
                    this.$message.warning(this.$t('domain.templateLoadFailed'));
                }
            }).catch(err => {
                this.templateLoading = false;
                console.error('获取模板列表失败', err);
                this.$message.error(this.$t('domain.templateLoadFailed'));
            });
        },

        // 处理模板选择变化
        handleTemplateChange(templateId) {
            // 根据选中的模板ID查找对应的模板信息
            this.selectedTemplate = this.templateList.find(item => item.id === templateId);
        },

        // 跳转到模板管理页面
        goToTemplateManage() {
            this.$router.push('/domain/template');
        },

        backToCart() {
            this.$router.push('/domain/register');
        },

        confirmOrder() {
            this.$refs.contactForm.validate((valid) => {
                if (valid) {
                    // 检查是否选择了模板
                    if (!this.selectedTemplate) {
                        this.$message.warning(this.$t('domain.pleaseSelectTemplate'));
                        return;
                    }

                    // 检查余额
                    if (this.isBalanceInsufficient) {
                        // this.$message.warning(this.$t('message.insufficientBalancePleaseRecharge') || '余额不足，请充值');
                        this.createModel = true; // 改为弹出充值对话框
                        return;
                    }

                    this.isSubmitting = true;

                    let domains = [];
                    this.cartItems.forEach((item) =>
                        domains.push({ 'domain': item.domain, 'year': item.period })
                    )


                    // 构建订单数据
                    const orderData = {
                        domain: domains,
                        registrant_id: this.selectedTemplate.registrantId, // 使用选中的模板ID
                        template_id: this.selectedTemplate.id, // 使用选中的模板ID
                    };

                    // 实际使用时，应该调用API提交订单
                    console.log('order data', orderData);

                    this.axios.post('/admin/domain/createOrder', orderData, {
                        token: true
                    }).then(res => {
                        console.log(res);

                        this.isSubmitting = false;
                        if (res.status === 200 && res.data.code === 1) {
                            // 订单创建成功
                            localStorage.removeItem('domainCartItems');
                            this.isSuccess = true;
                            // this.$message.success(this.$t('domain.orderSuccess'));
                            // this.$router.push({
                            //     name: 'domain_register',
                            //     query: { orderSuccess: true }
                            // });
                        } else {
                            // 订单创建失败
                            // this.$message.error(this.$t('domain.orderFailed'));
                        }
                    }).catch(err => {
                        this.isSubmitting = false;
                        console.error('创建订单失败', err);
                        // this.$message.error(this.$t('domain.orderFailed'));
                    });

                }
            });
        },

        goToDomainList() {
            this.$router.push('/domain/list');
        },
        gotoDomainRegister() {
            this.$router.push('/domain/register');
        },
        // 获取域名折扣数据
        async loadDomainDiscountData() {
            try {
                const res = await this.axios.post('/admin/user/getDomainRebateData', {}, { token: true });
                if (res.status === 200 && res.data && res.data.code === 1) {
                    // 将数组转换为以域名后缀为key的对象，便于快速查找
                    const discountMap = {};
                    if (res.data.data && res.data.data.list) {
                        res.data.data.list.forEach(item => {
                            if (item.type === 'register') {
                                discountMap[item.suffix] = item;
                            }
                        });
                    }
                    this.domainDiscountData = discountMap;
                    console.log('domainDiscountData', this.domainDiscountData);
                } else {
                    console.warn('获取域名折扣数据失败:', res.data?.msg || '未知错误');
                }
            } catch (error) {
                console.error('获取域名折扣数据失败:', error);
            }
        },
        // 计算折扣价格
        calculateDiscountPrice(domain, originalPrice) {
            if (!domain || !originalPrice) return null;
            
            // 提取域名后缀
            const suffix = domain.substring(domain.lastIndexOf('.'));
            const discountInfo = this.domainDiscountData[suffix];
            console.log('discountInfo', discountInfo);
            if (!discountInfo || !discountInfo.rebate) {
                return null; // 没有折扣
            }
            
            const discountRate = parseFloat(discountInfo.rebate);
            
            if (discountRate >= 100 || discountRate <= 0) {
                return null; // 无效的折扣率
            }
            
            
            const discountPrice = (originalPrice * discountRate / 100).toFixed(2);
            return {
                originalPrice: parseFloat(originalPrice),
                discountPrice: parseFloat(discountPrice),
                discountRate: discountRate,
                savings: (originalPrice - discountPrice).toFixed(2)
            };
        }
    },
    watch: {
        totalPrice(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.checkBalance(); // 总价变化时重新检查余额
            }
        },
    }
}
</script>

<style scoped>
.domain-checkout {
    max-width: 1200px;
    margin: 20px auto;
    padding: 0 20px;
}

.checkout-card {
    margin-bottom: 20px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.checkout-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.checkout-title {
    font-size: 20px;
    color: #333;
    margin: 0;
    font-weight: 500;
}

.section-title {
    font-size: 16px;
    color: #333;
    margin: 20px 0 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
}

.order-table {
    margin-bottom: 20px;
    border-radius: 4px;
}

.domain-info {
    display: flex;
    flex-direction: column;
}

.domain-name {
    font-weight: 500;
    color: #333;
}

.domain-period {
    font-size: 12px;
    color: #909399;
    margin-top: 5px;
}

.price {
    color: #ff6700;
    font-weight: 500;
}

.total-payment {
    display: flex;
    justify-content: flex-end;
    padding: 15px 0;
    font-size: 16px;
    font-weight: 500;
}

.total-price {
    color: #ff6700;
    margin-left: 10px;
    font-size: 20px;
}

.current-balance {
    display: flex;
    justify-content: flex-end;
    padding: 5px 0 15px; /* 调整上下边距 */
    font-size: 14px; /* 调整字体大小 */
    color: #606266; /* 调整字体颜色 */
}

.balance-amount {
    color: #409EFF; /* 余额数字颜色 */
    margin-left: 8px;
    font-weight: 500;
}

.balance-insufficient-tip {
    margin-top: 15px;
    margin-bottom: 10px;
}

.checkout-form {
    margin-bottom: 20px;
}

/* 模板选择样式 */
.template-option {
    display: flex;
    flex-direction: column;
    padding: 5px 0;
}

.template-name {
    font-weight: 500;
    color: #333;
    margin-bottom: 5px;
}

.template-contact {
    font-size: 12px;
    color: #909399;
}

.template-detail {
    margin-top: 15px;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 4px;
    border: 1px solid #ebeef5;
}

.detail-item {
    margin-bottom: 10px;
    line-height: 1.5;
}

.detail-item .label {
    color: #606266;
    font-weight: 500;
}

.detail-item .value {
    color: #333;
}

.no-template-tip {
    margin-top: 15px;
}

.payment-options {
    display: flex;
    margin-bottom: 20px;
}

.payment-options .el-radio {
    margin-right: 20px;
    padding: 10px 15px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    width: 120px;
    text-align: center;
}

.payment-options .el-radio.is-checked {
    border-color: #0052d9;
    background-color: #f0f7ff;
}

.payment-options i {
    margin-right: 5px;
    font-size: 16px;
}

.checkout-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
}

.checkout-actions .el-button {
    margin-left: 10px;
}

.checkout-actions .el-button--primary {
    background-color: #0052d9;
    border-color: #0052d9;
}
.balance-insufficient-tip .el-button--text {
    margin-left: 5px;
}

.deposit-dialog-content {
    padding: 10px 20px;
}

.deposit-dialog-content .wallet-section {
    margin-bottom: 25px;
    align-items: center; /* 垂直居中对齐 */
}

.deposit-dialog-content .wallet-label {
    font-weight: bold;
    margin-bottom: 8px;
    font-size: 14px;
}

.deposit-dialog-content .wallet-address-container {
    display: flex;
    align-items: center;
    background-color: #f5f7fa;
    padding: 8px 12px;
    border-radius: 4px;
}

.deposit-dialog-content .wallet-address {
    font-size: 15px;
    word-break: break-all;
    flex-grow: 1; /* 占据剩余空间 */
    margin-right: 10px;
}

.deposit-dialog-content .copy-button {
    margin-left: auto; /* 将复制按钮推到右边 */
}

.deposit-dialog-content .wallet-qrcode-container {
    display: flex;
    justify-content: center;
    align-items: center;
}

.deposit-dialog-content .wallet-qrcode {
    border: 1px solid #eee;
    padding: 5px;
    display: inline-block; /* 使边框包裹二维码 */
    margin: 0 auto;
}

.deposit-dialog-content .notice-section {
    margin-top: 20px;
    margin-bottom: 20px;
}

.deposit-dialog-content .notice-alert .notice-title ul {
    padding-left: 20px; /* 为列表添加内边距 */
    margin-top: 5px;
}

.deposit-dialog-content .notice-alert .notice-title li {
    margin-bottom: 5px; /* 列表项之间的间距 */
    line-height: 1.6;
}

.dialog-footer {
    text-align: right;
}

/* 新增充值表单样式 */
.recharge-form .el-form-item {
    margin-bottom: 20px;
}

.address-input input {
    font-family: monospace;
    font-size: 14px;
}

.qrcode-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 15px 0;
}

.qrcode-image {
    width: 160px;
    height: 160px;
    margin-bottom: 10px;
}

.qrcode-tip {
    font-size: 13px;
    color: #606266;
    text-align: center;
    margin-top: 10px;
}

.text-center {
    text-align: center;
}

.mt-3 {
    margin-top: 15px;
}

/* 折扣价格显示样式 */
.checkout-discount-price {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.checkout-original-price {
    color: #909399;
    text-decoration: line-through;
    font-size: 12px;
    margin-bottom: 2px;
}

.checkout-discount-price-value {
    color: #ff6700;
    font-weight: 500;
    font-size: 14px;
}

.checkout-normal-price .price {
    color: #ff6700;
    font-weight: 500;
}

/* 总价部分折扣样式 */
.original-total-price {
    display: flex;
    justify-content: flex-end;
    padding: 5px 0;
    font-size: 14px;
    color: #909399;
}

.original-total-price span:last-child {
    margin-left: 10px;
    text-decoration: line-through;
}

.discount-row {
    display: flex;
    justify-content: flex-end;
    padding: 5px 0;
    font-size: 14px;
    color: #67c23a;
}

.discount-amount {
    color: #67c23a;
    margin-left: 10px;
    font-weight: 600;
}
</style>